import React, { useEffect } from 'react';
import PhotoField from './PhotoField';
import ContactField from './ContactField';
import { conditionOf } from './formConditions';
import { getMissingFields, getReferencedFieldIds } from './formSchemaUtils';
import {
  getSchemaEntries,
  getSchemaFields,
  isSection,
  sectionFields,
  sectionTitle,
} from './formSections';
import { isMultiSelect, toSelectValues, toggleSelectValue } from './selectValue';
import FormHeaderBanner from './FormHeaderBanner';
import { getFormHeader } from './formHeader';

// How far down the app's own header sits, and how far each level of pinned
// title starts below the one it is filed under — a shade less than a title is
// tall, so consecutive ones overlap rather than leaving a slot for the page to
// scroll through between them. An outer title sits above the titles inside it,
// so a subsection's slides away under the chapter it belongs to rather than
// across it as its own section leaves the screen.
const APP_HEADER_REM = 4;
const HEADING_HEIGHT_REM = 2.5;
const MAX_PINNED_DEPTH = 2;

const pinnedTop = (depth) =>
  `${APP_HEADER_REM + Math.min(depth, MAX_PINNED_DEPTH) * HEADING_HEIGHT_REM}rem`;

const pinnedZ = (depth) => Math.max(21, 24 - depth);

const NOTHING_HIDDEN = new Set();

// The questions and sections of a form, drawn for filling in or for reading
// back. `hiddenIds` are the ones whose conditions aren't met (see
// useResolvedForm): they aren't drawn at all, so they can't be seen or
// answered, and a required one can't hold the form up. A conditional entry
// eases in as it appears, so the question that opens below an answer reads as
// that answer's doing rather than as the page jumping.
const FormFields = ({
  schema,
  formData,
  calculatedValues,
  hiddenIds = NOTHING_HIDDEN,
  onChange,
  disabled = false,
}) => {
  const entries = getSchemaEntries(schema);
  // Sections group the page; a calculation still reaches any question on the
  // form, wherever it was filed.
  const fields = getSchemaFields(schema);
  // What a calculation can still be waiting for: a hidden question can't be
  // answered, so it is never named as one.
  const shownFields = fields.filter((field) => !hiddenIds.has(field.id));
  // Auto-resize textareas when form data changes
  useEffect(() => {
    const textareas = document.querySelectorAll('textarea[data-field-type="text"]');
    textareas.forEach((textarea) => {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    });
  }, [formData, calculatedValues]);

  const renderField = (field) => {
    const value = formData[field.id] ?? '';

    switch (field.type) {
      case 'text':
        return (
          <div className="space-y-2">
            <textarea
              value={value}
              onChange={(e) => onChange(field.id, e.target.value)}
              onInput={(e) => {
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              required={field.required}
              disabled={disabled}
              className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none resize-none min-h-[40px] overflow-hidden disabled:opacity-70 disabled:cursor-not-allowed"
              placeholder="Enter your answer"
              rows={1}
              data-field-type="text"
            />
            {!disabled && Array.isArray(field.presets) && field.presets.length > 0 && (
              <div className="space-y-2">
                <div className="text-sm text-gray-300">Quick fill</div>
                {field.presets.length <= 5 ? (
                  <div className="flex flex-wrap gap-2">
                    {field.presets.map((preset, presetIndex) => {
                      const label = preset || `Preset ${presetIndex + 1}`;
                      const display = label.length > 15 ? `${label.slice(0, 15)}...` : label;
                      return (
                        <button
                          key={presetIndex}
                          type="button"
                          title={label}
                          onClick={() => onChange(field.id, preset)}
                          className="rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs px-3 py-1.5"
                        >
                          {display}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <select
                    defaultValue=""
                    onChange={(e) => {
                      const presetValue = e.target.value;
                      if (presetValue) {
                        onChange(field.id, presetValue);
                        e.target.value = '';
                      }
                    }}
                    className="w-full sm:w-auto bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:border-purple-500 focus:outline-none"
                  >
                    <option value="">Select a preset</option>
                    {field.presets.map((preset, presetIndex) => {
                      const label = preset || `Preset ${presetIndex + 1}`;
                      const display = label.length > 20 ? `${label.slice(0, 20)}...` : label;
                      return (
                        <option key={presetIndex} value={preset} title={label}>
                          {display}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
            )}
          </div>
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            required={field.required}
            disabled={disabled}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none disabled:opacity-70 disabled:cursor-not-allowed"
            placeholder="Enter a number"
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            required={field.required}
            disabled={disabled}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:border-purple-500 focus:outline-none disabled:opacity-70 disabled:cursor-not-allowed"
          />
        );

      case 'select': {
        const options = Array.isArray(field.options) ? field.options : [];

        // "Allow multiple answers" turns the dropdown into a checklist: every
        // ticked option is kept, and the answer is submitted as a list.
        if (isMultiSelect(field)) {
          const chosen = toSelectValues(value);
          if (options.length === 0) {
            return (
              <div className="bg-gray-700/40 border border-gray-600 rounded px-3 py-2 text-gray-400 italic">
                No options were added to this question.
              </div>
            );
          }
          return (
            <div className="space-y-2">
              <div className="space-y-1">
                {options.map((option, index) => (
                  <label
                    key={index}
                    className={`flex items-start gap-3 bg-gray-700 border rounded px-3 py-2 text-white transition-colors ${
                      chosen.includes(option) ? 'border-purple-500' : 'border-gray-600'
                    } ${disabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer hover:border-purple-400'}`}
                  >
                    <input
                      type="checkbox"
                      checked={chosen.includes(option)}
                      disabled={disabled}
                      onChange={() => onChange(field.id, toggleSelectValue(value, option, options))}
                      className="mt-1"
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
              {/* Native validation has nothing to attach to on a checkbox group
                  where any one box satisfies the requirement, so a hidden input
                  carries the message instead (same trick as PhotoField). */}
              {field.required && !disabled && chosen.length === 0 && (
                <input
                  type="text"
                  required
                  value=""
                  onChange={() => {}}
                  tabIndex={-1}
                  aria-hidden="true"
                  className="sr-only"
                  onInvalid={(e) => e.target.setCustomValidity('Please choose at least one option.')}
                  onInput={(e) => e.target.setCustomValidity('')}
                />
              )}
            </div>
          );
        }

        // A question switched back to single-answer may still hold a list from
        // an earlier submission; show the first entry rather than "[object]".
        const singleValue = Array.isArray(value) ? toSelectValues(value)[0] || '' : value;
        return (
          <select
            value={singleValue}
            onChange={(e) => onChange(field.id, e.target.value)}
            required={field.required}
            disabled={disabled}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white focus:border-purple-500 focus:outline-none disabled:opacity-70 disabled:cursor-not-allowed"
          >
            <option value="">Select an option</option>
            {options.map((option, index) => (
              <option key={index} value={option}>{option}</option>
            ))}
          </select>
        );
      }

      case 'photo':
        return (
          <PhotoField
            field={field}
            value={value}
            onChange={onChange}
            disabled={disabled}
          />
        );

      case 'contact':
        return (
          <ContactField
            field={field}
            value={value}
            onChange={onChange}
            disabled={disabled}
          />
        );

      case 'calculation': {
        const calculatedValue = calculatedValues?.[field.id] || '';
        const missingFields = getMissingFields(field.expression, shownFields, formData);
        // Built on a question these answers have hidden, it has nothing to
        // wait for that the person filling in could give it.
        const usesHidden = getReferencedFieldIds(field.expression, fields).some((id) => hiddenIds.has(id));
        let displayValue = calculatedValue;
        if (!displayValue && missingFields.length > 0) displayValue = `Waiting for: ${missingFields.join(', ')}`;
        else if (!displayValue && usesHidden) displayValue = 'Not available — it uses a question that isn’t shown';
        else if (!displayValue) displayValue = 'Calculating...';
        return (
          <div className="bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white">
            {displayValue}
          </div>
        );
      }

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(field.id, e.target.value)}
            required={field.required}
            disabled={disabled}
            className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none disabled:opacity-70 disabled:cursor-not-allowed"
            placeholder="Enter your answer"
          />
        );
    }
  };

  const reveal = (entry) => (conditionOf(entry) ? 'motion-safe:animate-reveal' : '');

  const renderQuestion = (field) => (
    <div key={field.id} className={`space-y-2 ${reveal(field)}`}>
      <label className="block text-white font-medium">
        {/* Question text, falling back to the field name on older forms
            that were built before the question became mandatory. */}
        {field.label?.trim() || field.name?.trim() || field.id}
        {field.required && <span className="text-red-400 ml-1">*</span>}
      </label>
      {renderField(field)}
    </div>
  );

  // A section, and anything filed inside it — subsections included, one indent
  // and one heading level further in each time.
  //
  // Each title is pinned under the app header and under the titles of the
  // sections it is inside: on a survey long enough to be split up, which part
  // you are answering is the first thing to scroll away and the hardest to work
  // out from the questions alone. Past the third level the titles would fill
  // the screen instead of labelling it, so the innermost ones share a slot.
  const renderSection = (entry, depth) => (
    <section key={entry.id} className={`space-y-4 ${reveal(entry)}`}>
      <h2
        style={{ top: pinnedTop(depth), zIndex: pinnedZ(depth) }}
        className={`sticky rounded-lg border border-purple-500/30 bg-gray-900/95 backdrop-blur-sm px-3 py-2 font-semibold text-purple-100 ${
          depth > 0 ? 'text-base' : 'text-lg'
        }`}
      >
        {sectionTitle(entry)}
      </h2>
      <div className="space-y-6 pl-3">
        {sectionFields(entry).map((child) => renderEntry(child, depth + 1))}
      </div>
    </section>
  );

  const renderEntry = (entry, depth) => {
    if (hiddenIds.has(entry.id)) return null;
    return isSection(entry) ? renderSection(entry, depth) : renderQuestion(entry);
  };

  return (
    <div className="space-y-6">
      {/* The form's report header, drawn wherever its questions are: filling a
          survey in, and reviewing a submitted one. */}
      <FormHeaderBanner
        header={getFormHeader(schema)}
        className="rounded-lg border border-gray-600 bg-gray-700/30 p-4"
      />
      {entries.map((entry) => renderEntry(entry, 0))}
    </div>
  );
};

export default FormFields;
